import { zodResolver } from '@hookform/resolvers/zod';
import { useTrade } from '@/lib/trade';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@/components/ui';
import { useAddTradeDefaults } from '@/features/job-types/job-types-panel';
import { request } from '@/lib/api';
import { useFeatures } from '@/lib/features';
import i18n, { intlLocale } from '@/lib/i18n';
import { customerSchema } from '@/lib/schemas';
import { useEmailEnabled } from '@/lib/use-email-enabled';
import { formatSlot, SlotPicker, type PickedSlot, type Readiness } from './slot-picker';

/**
 * הסכימה משקפת את `CreateManualTaskDto` בשרת אחד לאחד.
 *
 * `jobTypeTemplateId` הוא **חובה** שם, לא אופציונלי — התבנית היא מה
 * שקובע את ה-checklist, את המחירון ואת הכישור הנדרש לשיוך. משימה
 * בלי תבנית היא משימה שאי אפשר לחייב עליה ואי אפשר לשייך.
 */
const schema = z.object({
  // בלי לקוחות (ניהול משרד) — המשימה נפתחת על העסק עצמו, בשרת.
  customerId: z.string().uuid(i18n.t('tasks:new.pickCustomer')).optional(),
  jobTypeTemplateId: z.string().uuid(i18n.t('tasks:new.pickJobType')),
  title: z.string().trim().min(1, i18n.t('tasks:new.enterTitle')).max(200, i18n.t('tasks:new.max200')),
  description: z.string().trim().max(5000, i18n.t('tasks:new.max5000')).optional(),
});
type FormValues = z.infer<typeof schema>;

const templateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  requiredSkill: z.string().nullish(),
  durationMinutes: z.number().catch(120),
  leadTimeDays: z.number().catch(0),
});

const customerListSchema = z.object({ items: z.array(customerSchema) });

const shortDate = new Intl.DateTimeFormat(intlLocale(), {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'numeric',
});

/** עבודה שצריכה הכנה — רשום בתיאור, כדי שה"ממתין לחלקים" יהיה גלוי בכרטיס. */
function withPrepNote(
  description: string | undefined,
  readyFrom: string | null,
): string | undefined {
  const text = description?.trim() || '';
  if (!readyFrom) return text || undefined;
  const note = i18n.t('tasks:new.prepNote', { date: shortDate.format(new Date(`${readyFrom}T12:00:00Z`)) });
  return text ? `${text}\n\n${note}` : note;
}

export function NewTaskDialog() {
  const { t } = useTranslation(['tasks', 'common']);
  const trade = useTrade();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const email = useEmailEnabled();

  // נטענים רק כשהדיאלוג נפתח — אין טעם לשלוף לקוחות ותבניות
  // בכל טעינה של מסך הרשימה.
  const features = useFeatures();
  const hasCustomers = features.has('customers');
  const customers = useQuery({
    queryKey: ['customers', { forPicker: true }],
    queryFn: ({ signal }) => request('/customers?take=100', { schema: customerListSchema, signal }),
    enabled: open && hasCustomers,
  });

  const addDefaults = useAddTradeDefaults();
  const templates = useQuery({
    queryKey: ['job-type-templates'],
    queryFn: ({ signal }) =>
      request('/job-type-templates', { schema: z.array(templateSchema), signal }),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const [slot, setSlot] = useState<PickedSlot | null>(null);
  const [readiness, setReadiness] = useState<Readiness>({ readyFrom: null });

  useEffect(() => {
    if (!open) {
      reset();
      setSlot(null);
      setReadiness({ readyFrom: null });
    }
  }, [open, reset]);

  const customerId = watch('customerId');
  const templateId = watch('jobTypeTemplateId');
  const template = templates.data?.find((tpl) => tpl.id === templateId);

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      request<{ id: string }>('/tasks/manual', {
        method: 'POST',
        body: {
          ...values,
          description: withPrepNote(values.description, readiness.readyFrom),
          ...(slot
            ? { assignedToUserId: slot.userId, scheduledStart: slot.start, scheduledEnd: slot.end }
            : {}),
        },
        // יצירת משימה מפעילה שיוך טכנאי ומייל ללקוח. לחיצה כפולה
        // או ניסיון חוזר אחרי timeout לא אמורים לייצר שתי משימות.
        idempotencyKey: idempotencyKey,
      }),
    onSuccess: async () => {
      if (slot) toast.success(t('new.createdScheduled'), `${slot.userName} · ${formatSlot(slot)}`);
      else
        toast.success(
          t('new.created'),
          email.enabled
            ? t('new.createdHintEmail', { worker: trade.worker.one })
            : t('new.createdHint', { worker: trade.worker.one }),
        );
      setOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tasks'] }),
        qc.invalidateQueries({ queryKey: ['schedule'] }),
      ]);
    },
    onError: (err: Error) => toast.error(t('new.failed'), err.message),
  });

  // מפתח אחד לכל פתיחה של הדיאלוג, לא לכל רינדור.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `open` is a deliberate trigger: a fresh key per dialog opening, not a value the callback reads
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [open]);

  const onSubmit = handleSubmit((values) => {
    if (hasCustomers && !values.customerId) {
      setError('customerId', { message: t('new.pickCustomer') });
      return;
    }
    return create.mutateAsync(values).catch(() => undefined);
  });

  const loading = (hasCustomers && customers.isLoading) || templates.isLoading;
  const noTemplates = templates.data?.length === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-track="task.new">
          <Plus aria-hidden />
          {t('new.trigger')}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('new.title')}</DialogTitle>
          <DialogDescription>{t('new.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            {noTemplates ? (
              // מצב ריק אמיתי: אומר למה אי אפשר להמשיך ומה לעשות.
              <div
                role="alert"
                className="space-y-2.5 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-warning"
              >
                <p>{t('new.noTemplates')}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    loading={addDefaults.isPending}
                    onClick={() => addDefaults.mutate()}
                  >
                    <Sparkles aria-hidden />
                    {t('new.addRecommended', { trade: trade.label })}
                  </Button>
                  <Link
                    to="/settings/$tab"
                    params={{ tab: 'job-types' }}
                    className="font-medium underline underline-offset-2"
                  >
                    {t('new.setupManually')}
                  </Link>
                </div>
              </div>
            ) : null}

            {hasCustomers ? (
              <Field label={t('new.customer')} htmlFor="customerId" error={errors.customerId?.message} required>
                <Select
                  value={customerId ?? ''}
                  onValueChange={(v) => setValue('customerId', v, { shouldValidate: true })}
                  disabled={loading}
                >
                  <SelectTrigger id="customerId" aria-label={t('new.customer')}>
                    <SelectValue placeholder={loading ? t('common:state.loading') : t('new.selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.data?.items.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : null}

            <Field
              label={t('new.jobType')}
              htmlFor="jobTypeTemplateId"
              error={errors.jobTypeTemplateId?.message}
              hint={features.has('priceList') ? t('new.jobTypeHintPriceList') : t('new.jobTypeHint')}
              required
            >
              <Select
                value={templateId ?? ''}
                onValueChange={(v) => setValue('jobTypeTemplateId', v, { shouldValidate: true })}
                disabled={loading || noTemplates}
              >
                <SelectTrigger id="jobTypeTemplateId" aria-label={t('new.jobType')}>
                  <SelectValue placeholder={loading ? t('common:state.loading') : t('new.selectJobType')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.data?.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {customerId && template ? (
              <SlotPicker
                customerId={customerId}
                jobType={template}
                value={slot}
                onChange={setSlot}
                onReadinessChange={setReadiness}
              />
            ) : null}

            <Field label={t('new.titleLabel')} htmlFor="title" error={errors.title?.message} required>
              <Input id="title" placeholder={trade.key === 'OFFICE' ? t('new.titlePlaceholderOffice') : t('new.titlePlaceholder')} {...register('title')} />
            </Field>

            <Field label={t('new.descriptionLabel')} htmlFor="description" error={errors.description?.message}>
              <Textarea
                id="description"
                rows={3}
                placeholder={t('new.descriptionPlaceholder')}
                {...register('description')}
              />
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" size="sm" loading={isSubmitting} disabled={noTemplates}>
              {t('new.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
